import { createStore, type StoreApi } from 'zustand/vanilla';
import type {
  AdicionarPessoaEntrada,
  AlterarAtividadeEntrada,
  CriarAtividadeEntrada,
  EditarProcessoEntrada,
  LicitacaoDto,
  ModuloLayoutDto,
  UrlArquivoDto,
} from '@nexital/shared';
import { ErroApi, type AlvoLicitacao, type ApiLicitacao } from './portas';
import type { Salvamento } from './quadro-store';

export type EstadoLicitacao = {
  dados: LicitacaoDto | null;
  erroCarga: string | null;
  salvamento: Salvamento;
  gravacoesPendentes: number;
  /** Última recusa do servidor (formato, limite, duplicado…), para avisar a pessoa. */
  aviso: string | null;
  /** Nomes dos arquivos em envio. */
  enviando: string[];
  carregar(): Promise<void>;
  editarProcesso(campos: Omit<EditarProcessoEntrada, 'versao'>): Promise<boolean>;
  criarAtividade(entrada: CriarAtividadeEntrada): Promise<boolean>;
  alterarAtividade(uuid: string, entrada: Omit<AlterarAtividadeEntrada, 'versao'>): Promise<boolean>;
  adicionarPessoa(entrada: AdicionarPessoaEntrada): Promise<boolean>;
  removerPessoa(uuid: string): Promise<boolean>;
  moverModulos(modulos: Omit<ModuloLayoutDto, 'versao'>[]): Promise<boolean>;
  enviarDocumentos(arquivos: File[]): Promise<void>;
  urlDocumento(uuid: string): Promise<UrlArquivoDto | null>;
  dispensarAviso(): void;
};

type Loja = Pick<StoreApi<EstadoLicitacao>, 'setState' | 'getState'>;
type Ambiente = { api: ApiLicitacao; alvo: AlvoLicitacao; loja: Loja };

/**
 * Estado da licitação aberta. Cada gravação recarrega a licitação (a leitura é uma só e traz tudo);
 * a posição dos módulos muda na tela antes, para o arraste não "voltar".
 */
export function criarStoreLicitacao(api: ApiLicitacao, alvo: AlvoLicitacao) {
  return createStore<EstadoLicitacao>()((setState, getState) => {
    const amb: Ambiente = { api, alvo, loja: { setState, getState } };
    const versao = () => getState().dados?.processo.versao ?? 0;
    const versaoAtividade = (uuid: string) => getState().dados?.atividades.find((a) => a.uuid === uuid)?.versao ?? 0;
    return {
      dados: null,
      erroCarga: null,
      salvamento: 'salvo',
      gravacoesPendentes: 0,
      aviso: null,
      enviando: [],
      carregar: () => carregar(amb),
      editarProcesso: (campos) => gravar(amb, () => api.editarProcesso(alvo, { ...campos, versao: versao() })),
      criarAtividade: (entrada) => gravar(amb, () => api.criarAtividade(alvo, entrada)),
      alterarAtividade: (uuid, entrada) => gravar(amb, () => api.alterarAtividade(alvo, uuid, { ...entrada, versao: versaoAtividade(uuid) })),
      adicionarPessoa: (entrada) => gravar(amb, () => api.adicionarPessoa(alvo, entrada)),
      removerPessoa: (uuid) => gravar(amb, () => api.removerPessoa(alvo, uuid)),
      moverModulos: (modulos) => moverModulos(amb, modulos),
      enviarDocumentos: (arquivos) => enviarDocumentos(amb, arquivos),
      urlDocumento: (uuid) => api.urlDocumento(alvo, uuid).catch(() => null),
      dispensarAviso: () => setState({ aviso: null }),
    };
  });
}

async function carregar({ api, alvo, loja }: Ambiente) {
  try {
    loja.setState({ dados: await api.obterLicitacao(alvo), erroCarga: null });
  } catch (erro) {
    loja.setState({ erroCarga: (erro as Error).message });
  }
}

/** Grava, recarrega e mantém o indicador de salvamento (RQ-COL-007). Devolve se gravou. */
async function gravar(amb: Ambiente, trabalho: () => Promise<unknown>): Promise<boolean> {
  const { loja } = amb;
  loja.setState((s) => ({ gravacoesPendentes: s.gravacoesPendentes + 1, salvamento: 'salvando' }));
  try {
    await trabalho();
    loja.setState((s) => ({ salvamento: s.gravacoesPendentes > 1 ? 'salvando' : 'salvo' }));
    return true;
  } catch (erro) {
    const conflito = erro instanceof ErroApi && erro.status === 409;
    loja.setState({ salvamento: conflito ? 'conflito' : 'erro', aviso: (erro as Error).message });
    return false;
  } finally {
    loja.setState((s) => ({ gravacoesPendentes: s.gravacoesPendentes - 1 }));
    await carregar(amb);
  }
}

function moverModulos(amb: Ambiente, modulos: Omit<ModuloLayoutDto, 'versao'>[]) {
  const { loja, api, alvo } = amb;
  const dados = loja.getState().dados;
  if (!dados) return Promise.resolve(false);
  const versaoDe = new Map(dados.layout.map((m) => [m.modulo, m.versao]));
  const novos = modulos.map((m) => ({ ...m, versao: versaoDe.get(m.modulo) ?? 0 }));
  const outros = dados.layout.filter((m) => !modulos.some((n) => n.modulo === m.modulo));
  loja.setState({ dados: { ...dados, layout: [...outros, ...novos] } });
  return gravar(amb, () => api.alterarLayout(alvo, { modulos: novos }));
}

/** Envia um arquivo por vez (o servidor confere limite e duplicado de cada um). */
async function enviarDocumentos(amb: Ambiente, arquivos: File[]) {
  const { loja, api, alvo } = amb;
  for (const arquivo of arquivos) {
    loja.setState((s) => ({ enviando: [...s.enviando, arquivo.name] }));
    await gravar(amb, () => api.enviarDocumento(alvo, arquivo));
    loja.setState((s) => ({ enviando: s.enviando.filter((nome) => nome !== arquivo.name) }));
  }
}

export type StoreLicitacao = ReturnType<typeof criarStoreLicitacao>;
